import React, { useState, useEffect } from 'react';
import AuthShell from './AuthShell';
import { useNavigate } from 'react-router-dom';

const ForgotPasswordFront = ({ setResetEmail }) => {
  const [email, setEmail] = useState("");
  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [code, setCode] = useState("");
  const [showError, setShowError] = useState(false);
  const [apiMessage, setApiMessage] = useState("");
  const navigate = useNavigate();
  const [isLocked, setIsLocked] = useState(false);

  const handleSendEmail = async () => {
    setApiMessage("");
    setShowError(false);
    try {
      const response = await fetch("https://vicktoria.ai/api/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: email.toLowerCase() }),
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        setShowCodeEntry(true);
        setApiMessage("A password reset code has been sent!");
        setShowError(false);
      } else {
        setShowError(true);
      }
    } catch (err) {
      setShowError(true);
    }
  };

  // --- NEW UI FUNCTIONS ---
  const handleClearEntry = () => {
    setCode("");
  };

  const handleResendCode = () => {
    handleSendEmail();
  };

  useEffect(() => {
    const verifyCode = async () => {
      if (code.length === 6 && !isLocked) {
        try {
          const response = await fetch("https://vicktoria.ai/api/verify-code", {
            method: "POST",
            body: JSON.stringify({ email: email.toLowerCase(), userCode: code }),
            headers: { "Content-Type": "application/json" },
          });
          
          const data = await response.json();

          if (response.ok) {
            setResetEmail(email.toLowerCase()); 
            navigate('/reset-password');
          } else if (data.triggerNewCode) {
            setIsLocked(true);
            setCode(""); 
            setApiMessage("Limit reached. Generating a fresh code in 3s...");
            setShowError(false);

            setTimeout(() => {
              setIsLocked(false);
              handleSendEmail(); 
            }, 3000);
          } else {
            setShowError(true);
          setApiMessage(""); // STOP putting the error in the green apiMessage state
          }
        } catch (err) {
          setShowError(true);
        }
      }
    };
    verifyCode();
  }, [code, email, navigate, setResetEmail, isLocked]);
  const labelStyle = {
    color: '#2B4B6F',
    fontSize: '18px',
    marginBottom: '8px',
    fontWeight: '500'
  };

  const inputStyle = {
    width: '90%',
    maxWidth: '320px',
    padding: '12px',
    borderRadius: '10px',
    border: '2px solid #D1DEE6',
    backgroundColor: '#F8FAFC',
    textAlign: 'center',
    fontSize: '16px',
    outline: 'none',
    marginBottom: '20px',
    boxSizing: 'border-box'
  };

  const buttonStyle = {
    backgroundColor: '#5090AF',
    color: 'white',
    border: 'none',
    padding: '12px 40px',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '30px'
  };

  const boxStyle = {
    width: '45px',
    height: '55px',
    border: '1px solid #2B4B6F',
    display: 'inline-block',
    margin: '0 5px',
    backgroundColor: 'white',
    fontSize: '24px',
    lineHeight: '55px',
    textAlign: 'center'
  };

  return (
    <>
      <style>{`
        @media screen and (min-width: 300px) and (max-width: 600px) {
          .verification-box { width: 35px !important; height: 45px !important; line-height: 45px !important; margin: 0 3px !important; font-size: 18px !important; }
          .invisible-input { width: 200px !important; height: 45px !important; }
        }
      `}</style>
      <AuthShell title="Enter Your Email">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <p style={{ color: '#2B4B6F', fontSize: '16px', maxWidth: '300px', margin: '0 auto 20px auto', textAlign: 'center' }}>
            Enter the Email associated with your account to change your password
          </p>

          <input 
            type="email" 
            style={inputStyle} 
            placeholder="your@email.com" 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {!showCodeEntry ? (
            <button style={buttonStyle} onClick={handleSendEmail}>
              Send Recovery Email
            </button>
          ) : (
            <div style={{ textAlign: 'center', width: '100%' }}>
              <p style={{ color: '#7ED321', fontSize: '14px', marginBottom: '20px' }}>{apiMessage}</p>
              <div style={{ marginBottom: '25px', position: 'relative', display: 'flex', justifyContent: 'center' }}>
                <input
                  type="text" 
                  maxLength="6" 
                  autoFocus
                  value={code}
                  disabled={isLocked} 
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="invisible-input"
                  style={{ 
                    position: 'absolute', 
                    opacity: 0, 
                    width: '320px', 
                    height: '55px', 
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    zIndex: 2 
                  }}
                />
                {[...Array(6)].map((_, i) => (
                  <div 
                    className="verification-box" 
                    key={i} 
                    style={{
                      ...boxStyle,
                      borderColor: isLocked ? '#D1DEE6' : '#2B4B6F',
                      backgroundColor: isLocked ? '#F8FAFC' : 'white',
                      color: isLocked ? '#A0AEC0' : '#2B4B6F',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {code[i] || ""}
                  </div>
                ))}
              </div>

              {/* NEW ACTION BUTTONS */}
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                 <span 
                   onClick={handleClearEntry}
                   style={{ cursor: 'pointer', textDecoration: 'underline', color: '#2B4B6F', fontSize: '14px' }}
                 >
                   Clear Entry
                 </span>
                 <span 
                   onClick={handleResendCode}
                   style={{ cursor: 'pointer', textDecoration: 'underline', color: '#2B4B6F', fontSize: '14px' }}
                 >
                   Request New Code
                 </span>
              </div>

              {showError && <p style={{ color: '#D0021B', fontSize: '14px' }}>{apiMessage || "Code invalid or expired."}</p>}
            </div>
          )}

          <span style={{ color: '#2B4B6F', cursor: 'pointer', marginTop: '20px', fontSize: '14px', textDecoration: 'underline' }}
            onClick={() => navigate('/login')}>Back to Login</span>
        </div>
      </AuthShell>
    </>
  );
};

export default ForgotPasswordFront;